// =========================================================================
// BUSINESS WI-FI — GOOGLE ADS LANDING PAGE (/business-wifi)
// =========================================================================
//
// Conversion-focused landing page for business Wi-Fi / network work. Built
// from the same recipe as ManagedItServicesLandingPage in
// src/SightlineWebsite.tsx (ServiceHero → TrustBar → pain points → process →
// services → industries → internal links → why-us → work → service area →
// LeadCta → lead form → FAQ → sticky CTA).
//
// The kit components (ServiceHero, TrustBar, LeadForm, MobileStickyCta, …)
// and trackCta() are imported from SightlineWebsite.tsx, which exports them
// for exactly this purpose. LeadForm's success message (`p.text-emerald-600`
// plus the lead-form-success id) is the exact element GTM's Google Ads
// conversion trigger watches for — keep that markup byte-identical in any
// refactor. "business-wifi" is a PageKey (src/i18n/types.ts) with routes in
// src/i18n/routes.json, so setPage() and trackCta() accept it directly.

import { useState } from "react";
import {
  ArrowRight,
  ChevronRight,
  Globe,
  Headphones,
  Lock,
  MapPin,
  Network,
  Phone,
  Radio,
  Repeat,
  Router,
  Server,
  Shield,
  Signal,
  Users,
  Wifi,
  Wrench,
} from "lucide-react";
import { useT } from "../i18n";
import type { PageKey } from "../i18n";
import {
  LeadForm,
  MobileStickyCta,
  ServiceHero,
  TrustBar,
  IndustryGrid,
  WhyChooseUs,
  ServiceArea,
  LeadCta,
  SectionLabel,
  FeatureCard,
  PageLink,
  trackCta,
} from "../SightlineWebsite";


const PHONE = "(727) 283-6452";
const PHONE_HREF = "tel:17272836452";

// =========================================================================
// FAQ CONTENT — one array feeds both the visible accordion and the FAQPage
// JSON-LD below, so the on-page answers and the structured data can never
// drift apart.
// =========================================================================

export const businessWifiFaqs: Array<{ q: string; a: string }> = [
  {
    q: "Can you improve Wi-Fi coverage in a warehouse or large building?",
    a: "Yes. Large and industrial spaces are a regular part of our work. We assess how your building's construction affects signal, then plan access point placement around metal racking, machinery, and concrete walls instead of guessing from a floor plan.",
  },
  {
    q: "How many access points does my business need?",
    a: "It depends on the building, not a formula. Square footage, construction materials, layout, and how many devices connect at once all matter. The site assessment is how we answer this properly instead of selling you a fixed number.",
  },
  {
    q: "Can you fix an existing network or do we need new equipment?",
    a: "Often, yes. Misconfigured settings, poor placement, and interference can be corrected without replacing everything. When equipment genuinely needs to be replaced, we will show you why before recommending anything.",
  },
  {
    q: "Do you install the cabling for new access points?",
    a: "Yes. We run and terminate structured cabling for access points, cameras, and network hardware, so the same team handles both the cabling and the network configuration.",
  },
  {
    q: "Is business Wi-Fi different from consumer mesh kits?",
    a: "Consumer mesh kits are designed for homes and struggle with commercial device counts, security requirements, and building layouts. Business-grade equipment provides proper network separation, centralized management, and hardware we can support long-term.",
  },
  {
    q: "Do you offer ongoing support after installation?",
    a: "Yes, when you want it. Some clients prefer to manage the network themselves after handover; others keep us on for monitoring, changes, and troubleshooting. We will set up whichever arrangement fits your team.",
  },
];

// =========================================================================
// THE PAGE
// =========================================================================

export function BusinessWifiLanding({ setPage }: { setPage: (page: PageKey) => void }) {
  const t = useT();

  const scrollToLeadForm = () => {
    document
      .getElementById("business-wifi-lead-form")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const painPoints: Array<{ icon: typeof Phone; title: string }> = [
    { icon: Wifi, title: "Slow or unreliable Wi-Fi in parts of the building" },
    { icon: Phone, title: "Devices disconnecting during calls or transactions" },
    { icon: Users, title: "Wi-Fi that slows down as more devices connect" },
    { icon: Router, title: "Consumer-grade routers and mesh kits not built for business use" },
    { icon: Radio, title: "Poor access point placement and interference" },
    { icon: Repeat, title: "Employees unable to roam between access points without dropping" },
  ];

  const processSteps: string[] = [
    "We walk your building and measure where coverage actually reaches.",
    "We review your existing cabling, switches, and equipment.",
    "We determine coverage requirements based on how your space is used.",
    "We design the network around your building, not a one-size-fits-all kit.",
    "We install and configure the access points, switches, and gateway.",
    "We test coverage and performance in every area your team works.",
    "We stay available for ongoing support after the network is live.",
  ];


  return (
    // Bottom padding on mobile only, clearing the sticky call/consult bar.
    <main className="pb-24 lg:pb-0">
      <ServiceHero
        eyebrow="Serving Tampa Bay businesses"
        title="Fix Slow & Unreliable Business Wi-Fi"
        subtitle="Dead zones, dropped connections, and unreliable coverage in offices, warehouses, and commercial facilities. We find out why your Wi-Fi is failing and fix the underlying problem."
        primaryLabel="Request a Wi-Fi Assessment"
        onPrimary={() => {
          trackCta("wifi_assessment_click", "wifi_hero", "business-wifi");
          scrollToLeadForm();
        }}
        ctaName="wifi-hero"
        page="business-wifi"
      />

      <TrustBar
        items={[
          { icon: MapPin, label: "Local Tampa Bay Support" },
          { icon: Signal, label: "On-Site Coverage Assessment" },
          { icon: Server, label: "Business-Grade Equipment" },
          { icon: Headphones, label: "Ongoing Support Available" },
        ]}
      />

      {/* Problem / pain points */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t("The daily reality")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Wi-Fi Problems That Shouldn't Still Be Happening")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t(
              "If parts of your building have weak signal, calls drop mid-sentence, or the network grinds to a halt every afternoon, the problem is rarely the internet plan. It is usually how the network inside the building was designed and installed. Most of these issues sound familiar:"
            )}
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2">
          {painPoints.map(({ icon: Icon, title }) => (
            <div
              key={title}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                <Icon className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium text-slate-700">{t(title)}</span>
            </div>
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-slate-600 leading-relaxed">
          {t(
            "If your employees keep losing Wi-Fi, printers disappear from the network, or the same problems keep coming back, we can help find the underlying problem instead of applying another temporary fix."
          )}
        </p>
      </section>

      {/* Process */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Our process")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("How we fix business Wi-Fi")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "Every building is different, so we work through a clear sequence instead of selling equipment on the first visit."
              )}
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2">
            {processSteps.map((step, index) => (
              <div
                key={step}
                className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sm font-semibold text-sky-700 ring-1 ring-sky-100">
                  {index + 1}
                </span>
                <span className="pt-1 text-sm leading-relaxed text-slate-700">{t(step)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("What we do")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Business Wi-Fi & Network Services")}
            </h2>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={Signal}
              title={t("Wi-Fi site assessments")}
              text={t("On-site evaluation of coverage, interference, and current equipment to find out what is actually wrong.")}
            />
            <FeatureCard
              icon={Wifi}
              title={t("Access point installation")}
              text={t("Business-grade access points mounted and configured for even coverage across your space.")}
            />
            <FeatureCard
              icon={Network}
              title={t("Network switch & gateway setup")}
              text={t("Switches and gateways configured so traffic flows reliably between wired and wireless devices.")}
            />
            <FeatureCard
              icon={Lock}
              title={t("VLANs & guest Wi-Fi")}
              text={t("Separate networks for staff, guests, and devices like cameras or payment terminals.")}
            />
            <FeatureCard
              icon={Radio}
              title={t("Roaming & interference fixes")}
              text={t("Corrections for dropped connections when employees move between areas or access points.")}
            />
            <FeatureCard
              icon={Wrench}
              title={t("Existing network troubleshooting")}
              text={t("Diagnosis and repair of an installed network before we ever recommend new equipment.")}
            />
          </div>
        </div>
      </section>

      {/* Who we help */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Who we help")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Wi-Fi for Commercial Spaces of Every Shape")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t(
                "Every building interferes with signal differently. Concrete, metal racking, long hallways, and crowded device counts all change what a network needs. We design for the space you actually have."
              )}
            </p>
          </div>
          <div className="mt-10">
            <IndustryGrid
              items={[
                "Offices",
                "Warehouses",
                "Manufacturing",
                "Retail",
                "Restaurants",
                "Professional Offices",
                "Multi-Floor Buildings",
              ]}
            />
          </div>
        </div>
      </section>

      {/* Internal links to related services */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {t("Related network services")}
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <PageLink
              to="structured-cabling"
              setPage={setPage}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:border-sky-300 hover:bg-slate-50"
            >
              {t("View structured cabling services")}
              <ArrowRight className="h-4 w-4 text-sky-600" />
            </PageLink>
            <PageLink
              to="unifi-installer"
              setPage={setPage}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:border-sky-300 hover:bg-slate-50"
            >
              {t("UniFi installation & support")}
              <ArrowRight className="h-4 w-4 text-sky-600" />
            </PageLink>
          </div>
        </div>
      </section>

      {/* Why SightLine */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Why SightLine Networks")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Why Businesses Choose SightLine Networks")}
            </h2>
          </div>
          <div className="mt-12">
            <WhyChooseUs
              items={[
                {
                  icon: MapPin,
                  title: "Local Tampa Bay Team",
                  text: "We are based in Clearwater and work on-site across Tampa Bay, so you deal with people who know the area and your building.",
                },
                {
                  icon: Shield,
                  title: "Business-Focused Recommendations",
                  text: "We recommend what your operation actually needs, sized for your building and budget, before suggesting any new hardware.",
                },
                {
                  icon: Globe,
                  title: "One Partner for Network + IT + Cameras",
                  text: "Wi-Fi, cabling, IT support, and security cameras from the same team, so nothing falls between vendors.",
                },
                {
                  icon: Headphones,
                  title: "Responsive Support",
                  text: "When something goes wrong you reach people who know your network, not a script read from another time zone.",
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Real work / projects */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <SectionLabel>{t("Project spotlights")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Wi-Fi Work Around Tampa Bay")}
            </h2>
            <p className="mt-3 text-sm italic text-slate-500">
              {t("Project spotlights coming soon — real Tampa Bay project photos and details will be added here.")}
            </p>
          </div>
          {/*
            PLACEHOLDER — TODO(marketing): replace these tiles with real project
            photos (access point installs, network racks, warehouse coverage
            work, cable runs) once supplied. Do not fill with stock imagery —
            see the handoff notes for this page for what to send over.
          */}
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {[
              { icon: Server, label: "Office Wi-Fi installation" },
              { icon: Wifi, label: "Warehouse access point installation" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center"
              >
                <Icon className="h-7 w-7 text-slate-400" />
                <span className="text-sm font-medium text-slate-500">{t(label)}</span>
                <span className="text-xs uppercase tracking-wide text-slate-400">
                  {t("Photo coming soon")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <SectionLabel>{t("Service area")}</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {t("Serving Businesses Throughout Tampa Bay")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600 leading-relaxed">
            {t("SightLine Networks provides business Wi-Fi and network services throughout the Tampa Bay area, including:")}
          </p>
          <div className="mt-8">
            <ServiceArea cities={["Tampa", "Clearwater", "St. Petersburg", "Largo", "Pinellas Park", "Brandon"]} />
          </div>
        </div>
      </section>

      <LeadCta
        title="Ready for Wi-Fi that covers your whole building?"
        text="Tell us where coverage falls apart and what your team is dealing with. We will walk you through what an assessment involves and what it would take to fix it."
        primaryLabel="Request a Wi-Fi Assessment"
        onPrimary={() => {
          trackCta("wifi_assessment_click", "wifi_bottom", "business-wifi");
          scrollToLeadForm();
        }}
        ctaName="wifi-bottom"
        page="business-wifi"
      />

      {/* Lead form */}
      <section id="business-wifi-lead-form" className="mx-auto max-w-3xl scroll-mt-20 px-6 py-20">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t("Request Your Wi-Fi Assessment")}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {t("Fill out the form and our Tampa Bay team will follow up within one business day.")}
            </p>
          </div>
          <div className="mt-8">
            <LeadForm
              page="business-wifi"
              source="business-wifi-landing"
              formId="business-wifi-form"
              subject="Business Wi-Fi"
              submitLabel="Request a Wi-Fi Assessment"
            />
          </div>
        </div>
      </section>

      {/* FAQ — same array feeds the visible accordion and the FAQPage JSON-LD
          rendered below it. */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>{t("Frequently asked questions")}</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {t("Business Wi-Fi questions, answered")}
            </h2>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {businessWifiFaqs.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-900 marker:hidden">
                  <span>{t(q)}</span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(a)}</p>
              </details>
            ))}
          </div>

          <p className="mt-10 text-center text-sm text-slate-600">
            {t("Still have questions? Call us at")}{" "}
            <a
              href={PHONE_HREF}
              className="font-medium text-sky-700 transition hover:text-sky-800"
              onClick={() => trackCta("phone_click", "wifi_faq_contact", "business-wifi")}
            >
              {PHONE}
            </a>
            {t(", or send us a note through the form above.")}
          </p>
        </div>
      </section>

      <MobileStickyCta
        onConsultClick={() => {
          trackCta("wifi_assessment_click", "wifi_sticky", "business-wifi");
          scrollToLeadForm();
        }}
        ctaName="wifi"
        page="business-wifi"
        label="Free Wi-Fi Assessment"
      />
    </main>
  );
}